/**
 * Helpers compartidos para trabajar con grupos de modificadores y con el mapa
 * `selectedExtras` que se guarda en cada item del carro.
 *
 * El mapa `selectedExtras` usa llaves con el formato `${groupId}__${optionId}__${groupIndex}`,
 * donde `groupIndex` es la posición del grupo una vez ordenado por `order`. Como el índice
 * depende del orden, todas las vistas deben ordenar los grupos igual: para eso están
 * `sortModifierGroups` / `sortModifierOptions`.
 */

const byOrder = (a, b) =>
  (a.order === 0 ? 0 : a.order || Number.MAX_SAFE_INTEGER) - (b.order === 0 ? 0 : b.order || Number.MAX_SAFE_INTEGER);

/**
 * Ordena los grupos de modificadores sin mutar el arreglo recibido.
 */
export function sortModifierGroups(modifiers) {
  return [...(modifiers || [])].sort(byOrder);
}

/**
 * Ordena las opciones de un grupo sin mutar el arreglo recibido.
 */
export function sortModifierOptions(options) {
  return [...(options || [])].sort(byOrder);
}

/**
 * Llave con la que se guarda una opción dentro de `selectedExtras`.
 */
export function buildExtraKey(groupId, optionId, groupIndex) {
  return `${groupId}__${optionId}__${groupIndex}`;
}

/**
 * Obtiene el valor guardado para una opción tolerando formatos antiguos y
 * diferencias de `groupIndex` (carros creados con otro orden de grupos).
 *
 * @returns {number|boolean|undefined} el valor crudo guardado, o undefined si no hay selección
 */
export function resolveExtraValue(selectedExtras, extraGroup, option, groupIndex) {
  if (!selectedExtras || !extraGroup || !option) return undefined;

  const currentKey = buildExtraKey(extraGroup.id, option.id, groupIndex);
  if (selectedExtras[currentKey] !== undefined) return selectedExtras[currentKey];

  // Mismo grupo y opción pero guardado con otro índice de grupo
  const prefix = `${extraGroup.id}__${option.id}__`;
  const matchingKey = Object.keys(selectedExtras).find(key => key.startsWith(prefix));
  if (matchingKey !== undefined) return selectedExtras[matchingKey];

  // Formatos antiguos
  const legacyKey = `${groupIndex}_${option.id}`;
  if (selectedExtras[legacyKey] !== undefined) return selectedExtras[legacyKey];
  return selectedExtras[option.id];
}

/**
 * Cantidad seleccionada de una opción, normalizando booleanos a 1.
 */
export function getExtraQuantity(selectedExtras, extraGroup, option, groupIndex) {
  const value = resolveExtraValue(selectedExtras, extraGroup, option, groupIndex);
  if (value === true) return 1;
  if (typeof value === 'number' && value > 0) return value;
  return 0;
}

/**
 * Convierte un `selectedExtras` guardado (posiblemente con llaves antiguas o con
 * otro `groupIndex`) al formato de llaves actual para los grupos entregados.
 *
 * @param {Array} sortedGroups grupos ya ordenados con `sortModifierGroups`
 * @param {Object} selectedExtras mapa guardado en el item del carro
 * @returns {Object} mapa normalizado, sólo con las opciones que tienen cantidad > 0
 */
export function normalizeSelectedExtras(sortedGroups, selectedExtras) {
  const normalized = {};
  if (!selectedExtras) return normalized;

  (sortedGroups || []).forEach((extraGroup, groupIndex) => {
    (extraGroup.options || []).forEach(option => {
      const quantity = getExtraQuantity(selectedExtras, extraGroup, option, groupIndex);
      if (quantity > 0) normalized[buildExtraKey(extraGroup.id, option.id, groupIndex)] = quantity;
    });
  });

  return normalized;
}

/**
 * Indica si un producto tiene grupos de modificadores válidos (y por lo tanto
 * necesita pasar por la vista de detalle antes de agregarse al carro).
 *
 * @param {Object} product producto a evaluar
 * @param {Function} getProductModifiers resolutor de ids de modificadores a grupos válidos
 */
export function hasProductModifiers(product, getProductModifiers) {
  if (!product || !product.modifiers || product.modifiers.length === 0) return false;
  if (typeof getProductModifiers !== 'function') return true;
  const groups = getProductModifiers(product.modifiers);
  return !!(groups && groups.length > 0);
}
